// ============================================================================
// src/lib/db/admin/dld-diagnostics.ts
// PV-1052 · M4 — ADR-055 Lot 3 : non-blocking DLD identity diagnostic.
//
// Thin typed wrapper over the admin_diagnose_deal_identity RPC (SECURITY
// DEFINER, admin OR super_admin guard). Shares _dld_resolve_identity with the
// matching engine server-side, so what the admin sees here is exactly what
// the benchmark engine will use. Purely informative — publication is never
// gated on the resolution state.
// ============================================================================

import { getSupabaseClient } from '@/lib/supabase';

import type { DdmSupabaseClient } from '@/lib/supabase';

export type DealIdentityResolutionLabel =
  | 'exact_building'
  | 'alias_building'
  | 'alias_group'
  | 'community_fallback'
  | 'zone_fallback'
  | 'unresolved';

export type DealIdentityDiagnostic = {
  resolution_label: DealIdentityResolutionLabel;
  canonical_building_key: string | null;
  group_key: string | null;
  group_building_keys: string[];
  effective_community_key: string | null;
  effective_zone_key: string | null;
  building_source: string;
  community_source: string;
  zone_source: string;
};

export async function diagnoseDealIdentity(
  dealId: string,
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<DealIdentityDiagnostic> {
  const { data, error } = await client.rpc('admin_diagnose_deal_identity', {
    p_deal_id: dealId,
  });
  if (error) throw error;
  return data as unknown as DealIdentityDiagnostic;
}

export type BackfillKeyChange = { before: string | null; after: string | null };

export type BackfillReport = {
  dry_run: boolean;
  updated_count: number;
  deals: Array<{
    deal_id: string;
    reference_id: string;
    building_key: BackfillKeyChange;
    community_key: BackfillKeyChange;
    zone_key: BackfillKeyChange;
    building_source: string;
    community_source: string;
    zone_source: string;
  }>;
};

/**
 * ADR-055 — explicit resolver-driven backfill of deals location keys.
 * Dry-run by default (report only, zero writes); `apply` writes keys-only
 * (editorial labels preserved), audits once and recomputes ONLY the touched
 * deals. Never overwrites a non-NULL key; never invents a building from a
 * group alias.
 */
export async function backfillDealLocationKeys(
  dryRun: boolean,
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<BackfillReport> {
  const { data, error } = await client.rpc('admin_backfill_deal_location_keys', {
    p_dry_run: dryRun,
  });
  if (error) throw error;
  return data as unknown as BackfillReport;
}

// ---------------------------------------------------------------------------
// ADR-055 Lot 6 — read-only DLD data quality report (Settings > DLD Matching).
// ---------------------------------------------------------------------------

export type DataQualityReport = {
  generated_at: string;
  freshness: {
    total_rows: number;
    ready_rows: number;
    earliest_date: string | null;
    latest_date: string | null;
    latest_ready_date: string | null;
  };
  coverage: Array<{
    building_key: string;
    /** Referential announced count — DIAGNOSTIC ONLY, never fed to the benchmark. */
    announced_count: number;
    loaded_total: number;
    loaded_ready: number;
    latest_loaded_date: string | null;
  }>;
  resolution: Array<{
    reference_id: string;
    type: 'ready' | 'off_plan';
    resolution_label: DealIdentityResolutionLabel;
    persisted_scope: string | null;
    persisted_confidence: string | null;
  }>;
  cohort_gaps: Array<{
    reference_id: string;
    scope: string | null;
    confidence: string | null;
  }>;
};

export async function fetchDldDataQualityReport(
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<DataQualityReport> {
  const { data, error } = await client.rpc('admin_dld_data_quality_report');
  if (error) throw error;
  return data as unknown as DataQualityReport;
}
